/**
 * Insights Utility Functions
 * Computes top performers, best sellers, and growth metrics
 */

import { pincodes } from './mockData/stores';
import { products } from './mockData/products';
import { categories } from './mockData/categories';

/**
 * Get top cities by GMV
 * @param {Array} orders - Filtered orders array
 * @param {number} topN - Number of top items to return
 * @returns {Array} Top cities with metrics
 */
export function getTopCitiesByGMV(orders, topN = 5) {
    if (!orders || orders.length === 0) return [];

    const cityMap = {};

    orders.forEach(order => {
        const cityId = order.cityId;
        if (!cityId) return;

        if (!cityMap[cityId]) {
            cityMap[cityId] = {
                id: cityId,
                name: order.cityName || cityId,
                gmv: 0,
                orders: 0,
                volume: 0,
            };
        }

        cityMap[cityId].gmv += order.total || 0;
        cityMap[cityId].orders += 1;
        cityMap[cityId].volume += order.itemCount || 0;
    });

    const sorted = Object.values(cityMap)
        .sort((a, b) => b.gmv - a.gmv)
        .slice(0, topN);

    // Calculate share percentages
    const totalGMV = sorted.reduce((sum, c) => sum + c.gmv, 0);
    sorted.forEach(city => {
        city.sharePercent = totalGMV > 0 ? (city.gmv / totalGMV) * 100 : 0;
    });

    return sorted;
}

/**
 * Get top stores by GMV
 * @param {Array} orders - Filtered orders array
 * @param {number} topN - Number of top items to return
 * @returns {Array} Top stores with metrics
 */
export function getTopStoresByGMV(orders, topN = 5) {
    if (!orders || orders.length === 0) return [];

    const storeMap = {};

    orders.forEach(order => {
        const storeId = order.storeId;
        if (!storeId) return;

        if (!storeMap[storeId]) {
            storeMap[storeId] = {
                id: storeId,
                name: order.storeName || storeId,
                area: order.cityName || '',
                gmv: 0,
                orders: 0,
                volume: 0,
            };
        }

        storeMap[storeId].gmv += order.total || 0;
        storeMap[storeId].orders += 1;
        storeMap[storeId].volume += order.itemCount || 0;
    });

    const sorted = Object.values(storeMap)
        .sort((a, b) => b.gmv - a.gmv)
        .slice(0, topN);

    // Calculate share percentages
    const totalGMV = sorted.reduce((sum, s) => sum + s.gmv, 0);
    sorted.forEach(store => {
        store.sharePercent = totalGMV > 0 ? (store.gmv / totalGMV) * 100 : 0;
    });

    return sorted;
}

/**
 * Get top pincodes by GMV
 * @param {Array} orders - Filtered orders array
 * @param {number} topN - Number of top items to return
 * @returns {Array} Top pincodes with metrics
 */
export function getTopPincodesByGMV(orders, topN = 5) {
    if (!orders || orders.length === 0) return [];

    const pincodeMap = {};

    orders.forEach(order => {
        const pincode = order.pincode;
        if (!pincode) return;

        if (!pincodeMap[pincode]) {
            const pincodeData = pincodes.find(p => p.code === pincode);
            pincodeMap[pincode] = {
                code: pincode,
                name: pincodeData?.area || pincode,
                area: pincodeData?.area || '',
                gmv: 0,
                orders: 0,
                volume: 0,
            };
        }

        pincodeMap[pincode].gmv += order.total || 0;
        pincodeMap[pincode].orders += 1;
        pincodeMap[pincode].volume += order.itemCount || 0;
    });

    const sorted = Object.values(pincodeMap)
        .sort((a, b) => b.gmv - a.gmv)
        .slice(0, topN);

    // Calculate share percentages
    const totalGMV = sorted.reduce((sum, p) => sum + p.gmv, 0);
    sorted.forEach(pincode => {
        pincode.sharePercent = totalGMV > 0 ? (pincode.gmv / totalGMV) * 100 : 0;
    });

    return sorted;
}

/**
 * Get COD percentage by pincode
 * @param {Array} orders - Filtered orders array
 * @param {number} topN - Number of top items to return
 * @returns {Array} Pincodes sorted by COD percentage (highest first)
 */
export function getCODPercentageByPincode(orders, topN = 5) {
    if (!orders || orders.length === 0) return [];

    const pincodeMap = {};

    orders.forEach(order => {
        const pincode = order.pincode;
        if (!pincode) return;

        if (!pincodeMap[pincode]) {
            const pincodeData = pincodes.find(p => p.code === pincode);
            pincodeMap[pincode] = {
                code: pincode,
                name: pincodeData?.area || pincode,
                totalOrders: 0,
                codOrders: 0,
            };
        }

        pincodeMap[pincode].totalOrders += 1;
        if (order.paymentMethod === 'COD') {
            pincodeMap[pincode].codOrders += 1;
        }
    });

    return Object.values(pincodeMap)
        .map(p => ({
            ...p,
            codPercent: p.totalOrders > 0 ? (p.codOrders / p.totalOrders) * 100 : 0,
        }))
        .sort((a, b) => b.codPercent - a.codPercent)
        .slice(0, topN);
}

/**
 * Get top products in scope
 * @param {Array} orders - Filtered orders array
 * @param {number} topN - Number of top items to return
 * @param {string} sortBy - 'value' or 'volume'
 * @returns {Array} Top products with metrics
 */
export function getTopProductsInScope(orders, topN = 5, sortBy = 'value') {
    if (!orders || orders.length === 0) return [];

    const productMap = {};

    orders.forEach(order => {
        if (!order.items || !Array.isArray(order.items)) return;

        order.items.forEach(item => {
            const productId = item.productId;
            if (!productId) return;

            if (!productMap[productId]) {
                const product = products.find(p => p.id === productId);
                const category = categories.find(c => c.id === item.categoryId);
                productMap[productId] = {
                    productId,
                    name: item.productName || product?.name || productId,
                    category: category?.shortName || item.categoryName || 'Other',
                    categoryColor: category?.color || '#808080',
                    revenue: 0,
                    volume: 0,
                };
            }

            productMap[productId].revenue += item.total || 0;
            productMap[productId].volume += item.quantity || 0;
        });
    });

    const allProducts = Object.values(productMap);
    const totalRevenue = allProducts.reduce((sum, p) => sum + p.revenue, 0);
    const totalVolume = allProducts.reduce((sum, p) => sum + p.volume, 0);

    // Add share percentages
    allProducts.forEach(product => {
        product.revenueShare = totalRevenue > 0 ? (product.revenue / totalRevenue) * 100 : 0;
        product.volumeShare = totalVolume > 0 ? (product.volume / totalVolume) * 100 : 0;
    });

    // Sort by requested metric
    const sortField = sortBy === 'volume' ? 'volume' : 'revenue';
    return allProducts
        .sort((a, b) => b[sortField] - a[sortField])
        .slice(0, topN);
}

/**
 * Calculate growth compared to previous period
 * @param {Array} currentOrders - Orders from current period
 * @param {Array} previousOrders - Orders from previous period
 * @returns {Object} Growth metrics
 */
export function getGrowthComparedToPreviousPeriod(currentOrders, previousOrders) {
    const current = {
        gmv: 0,
        orders: 0,
        volume: 0,
    };

    const previous = {
        gmv: 0,
        orders: 0,
        volume: 0,
    };

    if (currentOrders && currentOrders.length > 0) {
        currentOrders.forEach(order => {
            current.gmv += order.total || 0;
            current.orders += 1;
            current.volume += order.itemCount || 0;
        });
    }

    if (previousOrders && previousOrders.length > 0) {
        previousOrders.forEach(order => {
            previous.gmv += order.total || 0;
            previous.orders += 1;
            previous.volume += order.itemCount || 0;
        });
    }

    const calculateGrowth = (curr, prev) => {
        if (prev === 0) return curr > 0 ? 100 : 0;
        return ((curr - prev) / prev) * 100;
    };

    return {
        gmvGrowth: calculateGrowth(current.gmv, previous.gmv),
        ordersGrowth: calculateGrowth(current.orders, previous.orders),
        volumeGrowth: calculateGrowth(current.volume, previous.volume),
        current,
        previous,
    };
}

/**
 * Get previous period orders based on current date range
 * @param {Array} allOrders - All orders
 * @param {Date} startDate - Current period start
 * @param {Date} endDate - Current period end
 * @returns {Array} Orders from previous period of same length
 */
export function getPreviousPeriodOrders(allOrders, startDate, endDate) {
    if (!allOrders || !startDate || !endDate) return [];

    const periodLength = endDate.getTime() - startDate.getTime();
    const prevEnd = new Date(startDate.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - periodLength);

    const prevStartStr = prevStart.toISOString().split('T')[0];
    const prevEndStr = prevEnd.toISOString().split('T')[0];

    return allOrders.filter(order => {
        return order.date >= prevStartStr && order.date <= prevEndStr;
    });
}

/**
 * Get worst performers by GMV growth
 * @param {Array} currentOrders - Current period orders
 * @param {Array} previousOrders - Previous period orders
 * @param {string} groupKey - 'cityId' or 'pincode'
 * @param {number} topN - Number of worst items to return
 * @returns {Array} Worst performers with negative growth
 */
export function getWorstPerformersByGrowth(currentOrders, previousOrders, groupKey, topN = 5) {
    if (!currentOrders || currentOrders.length === 0) return [];

    const currentMap = {};
    const previousMap = {};

    // Aggregate current period
    currentOrders.forEach(order => {
        const key = order[groupKey];
        if (!key) return;
        if (!currentMap[key]) currentMap[key] = { gmv: 0, orders: 0 };
        currentMap[key].gmv += order.total || 0;
        currentMap[key].orders += 1;
    });

    // Aggregate previous period
    if (previousOrders && previousOrders.length > 0) {
        previousOrders.forEach(order => {
            const key = order[groupKey];
            if (!key) return;
            if (!previousMap[key]) previousMap[key] = { gmv: 0, orders: 0 };
            previousMap[key].gmv += order.total || 0;
            previousMap[key].orders += 1;
        });
    }

    // NOTE: This part expects getCityName to be passed in, or the data has it. 
    // To keep it simple, if groupKey is cityId/storeId, we shouldn't execute this block 
    // or we should map it before calling. For now, just use the key or order name.
    const results = Object.keys(currentMap).map(key => {
        const current = currentMap[key];
        const previous = previousMap[key] || { gmv: 0, orders: 0 };

        let name = key;
        const sampleOrder = currentOrders.find(o => o[groupKey] === key) || previousOrders?.find(o => o[groupKey] === key);

        if (groupKey === 'cityId') {
            name = sampleOrder?.cityName || key;
        } else if (groupKey === 'storeId') {
            name = sampleOrder?.storeName || key;
        } else if (groupKey === 'pincode') {
            const pincodeData = pincodes.find(p => p.code === key);
            name = pincodeData?.area || key;
        }

        const growth = previous.gmv > 0
            ? ((current.gmv - previous.gmv) / previous.gmv) * 100
            : (current.gmv > 0 ? 100 : 0);

        return {
            id: key,
            name,
            currentGMV: current.gmv,
            previousGMV: previous.gmv,
            growth,
            orders: current.orders,
        };
    });

    // Return worst performers (lowest growth, including negative)
    return results
        .sort((a, b) => a.growth - b.growth)
        .slice(0, topN);
}

/**
 * Get scope label based on applied filters
 * @param {Object} appliedFilters - Applied filter object
 * @param {Array} orders - Orders data to derive real names from
 * @returns {string} Scope label (Global, City, Store, Pincode)
 */
export function getScopeLabel(appliedFilters, orders) {
    if (!appliedFilters) return 'Global';

    if (appliedFilters.pincodes?.length > 0) {
        return appliedFilters.pincodes.length === 1
            ? `Pincode ${appliedFilters.pincodes[0]}`
            : `${appliedFilters.pincodes.length} Pincodes`;
    }

    if (appliedFilters.stores?.length > 0) {
        if (appliedFilters.stores.length === 1) {
            const storeOrder = orders?.find(o => o.storeId === appliedFilters.stores[0]);
            return storeOrder?.storeName || appliedFilters.stores[0];
        }
        return `${appliedFilters.stores.length} Stores`;
    }

    if (appliedFilters.cities?.length > 0) {
        const cityOrder = orders?.find(o => o.cityId === appliedFilters.cities[0]);
        if (appliedFilters.cities.length === 1) {
            return cityOrder?.cityName || appliedFilters.cities[0];
        }
        return `${appliedFilters.cities.length} Cities`;
    }

    return 'Global';
}

export default {
    getTopCitiesByGMV,
    getTopStoresByGMV,
    getTopPincodesByGMV,
    getCODPercentageByPincode,
    getTopProductsInScope,
    getGrowthComparedToPreviousPeriod,
    getPreviousPeriodOrders,
    getWorstPerformersByGrowth,
    getScopeLabel,
};
